import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { useProjectsStore } from '../store/projectsStore';
import { useCustomersStore } from '../store/customersStore';
import { useUsersStore } from '../store/usersStore';

interface AppInitializerProps {
  children: React.ReactNode;
}

export function AppInitializer({ children }: AppInitializerProps) {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  
  const fetchProjects = useProjectsStore((state) => state.fetchProjects);
  const fetchCustomers = useCustomersStore((state) => state.fetchCustomers);
  const initializeUsersStore = useUsersStore((state) => state.initialize);
  
  // Initialize essential application data
  useEffect(() => {
    // Only initialize data when user is authenticated
    if (!user) {
      setIsLoading(false);
      return;
    }
    
    // Prevent multiple initializations
    if (isInitialized) {
      setIsLoading(false);
      return;
    }
    
    const initializeApp = async () => {
      try {
        setIsLoading(true);

        // Portal users: authStore already loaded profile - skip PM users directory fetch.
        if (user.role === 'customer') {
          setIsInitialized(true);
          setIsLoading(false);
          return;
        }

        // Site engineers: lightweight init only
        if (user.role === 'site_engineer') {
          await initializeUsersStore();
        } else {
          // Load data concurrently for better performance
          await Promise.all([
            initializeUsersStore(),
            fetchProjects(),
            fetchCustomers()
          ]);
        }
        
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize application data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeApp();
  }, [user, fetchProjects, fetchCustomers, initializeUsersStore, isInitialized]);

  // Inactivity session timeout - tracked across every tab of this browser (not just this one).
  //
  // Why: all tabs share one persisted Supabase session (persistSession: true in src/lib/supabase.ts).
  // supabase-js broadcasts SIGNED_OUT to every tab whenever ANY tab calls signOut() - that's true
  // regardless of {scope}, since it's driven by the shared session being removed from localStorage,
  // not by server-side token revocation breadth. So a per-tab timer that only watched *its own*
  // mouse/keyboard events would sign a user out of a tab they're actively typing in the moment some
  // other, genuinely-idle tab of theirs (e.g. a stale tab left open from earlier) hit 30 minutes.
  // Recording activity in a shared localStorage key, and re-checking against that shared value
  // (via both a 'storage' event and a self-correcting recheck timer) makes every tab agree on
  // whether the user is "active" - so real activity anywhere keeps every tab's session alive, and
  // only a browser where every single tab has been idle for the full timeout actually logs out.
  useEffect(() => {
    if (!user) {
      setShowTimeoutWarning(false);
      return;
    }

    const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
    const WARNING_BEFORE_MS = 60 * 1000;
    const LAST_ACTIVITY_KEY = 'pm-last-activity';

    let recheckTimer: ReturnType<typeof setTimeout> | null = null;
    let warningTimer: ReturnType<typeof setTimeout> | null = null;

    const clearTimers = () => {
      if (recheckTimer) clearTimeout(recheckTimer);
      if (warningTimer) clearTimeout(warningTimer);
    };

    const readLastActivity = (): number => {
      try {
        const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
        const parsed = raw ? Number(raw) : NaN;
        return Number.isFinite(parsed) ? parsed : Date.now();
      } catch {
        return Date.now();
      }
    };

    const writeLastActivity = () => {
      try {
        localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      } catch {
        /* private mode / quota - this tab still tracks the timeout locally below */
      }
    };

    const handleAutoLogout = async () => {
      setShowTimeoutWarning(false);
      await logout();
    };

    // Re-evaluates against the shared last-activity time and reschedules - self-correcting, so a
    // timer that fires late (e.g. a backgrounded tab's setTimeout got throttled) or a missed
    // 'storage' event still resolves to the right outcome instead of trusting stale local state.
    const scheduleFromLastActivity = () => {
      clearTimers();
      const remaining = SESSION_TIMEOUT_MS - (Date.now() - readLastActivity());

      if (remaining <= 0) {
        void handleAutoLogout();
        return;
      }

      setShowTimeoutWarning(remaining <= WARNING_BEFORE_MS);
      const untilWarning = remaining - WARNING_BEFORE_MS;
      if (untilWarning > 0) warningTimer = setTimeout(() => setShowTimeoutWarning(true), untilWarning);
      recheckTimer = setTimeout(scheduleFromLastActivity, remaining);
    };

    const recordActivity = () => {
      writeLastActivity();
      setShowTimeoutWarning(false);
      scheduleFromLastActivity();
    };

    const onStorageActivity = (e: StorageEvent) => {
      if (e.key === LAST_ACTIVITY_KEY) scheduleFromLastActivity();
    };

    const activityEvents: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ];

    const onActivity = () => recordActivity();
    activityEvents.forEach((eventName) => window.addEventListener(eventName, onActivity, { passive: true }));
    window.addEventListener('storage', onStorageActivity);
    recordActivity();

    return () => {
      clearTimers();
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, onActivity));
      window.removeEventListener('storage', onStorageActivity);
    };
  }, [user, logout]);
  
  // Bootstrap loader only for signed-in staff - never block public pages on auth init.
  const showBootstrapLoader = !!user && !isInitialized && isLoading;
  if (showBootstrapLoader) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">Loading application data...</p>
        </div>
      </div>
    );
  }
  
  // Render children once initialized or when user is not authenticated
  return (
    <>
      {children}
      {showTimeoutWarning && user && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl border border-gray-200">
            <h3 className="text-lg font-semibold text-[#09175d]">Session Expiring Soon</h3>
            <p className="mt-2 text-sm text-[#4f5759]">
              You have been inactive for a while. You will be logged out automatically in about 1 minute.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => logout()}
                className="btn btn-secondary"
              >
                Logout now
              </button>
              <button
                onClick={() => setShowTimeoutWarning(false)}
                className="btn btn-primary"
              >
                Continue Session
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
} 